/// <reference types="vite/client" />
import type { Post, DetailedPost, PageConfig } from "../types.ts";
import { API_ENDPOINTS } from "./constants.ts";
import { decodeHtmlEntities } from "./html.ts";

const getRecommendationEndpoint = () =>
  `${API_ENDPOINTS.BASE_URL}${API_ENDPOINTS.RECOMMENDATIONS}`;
const getBlueConicRecommendationEndpoint = () =>
  `${API_ENDPOINTS.BASE_URL}${API_ENDPOINTS.BLUECONIC_RECOMMENDATIONS}`;

export type BlueConicRecommendationResponse = {
  posts: Post[];
  trackingPixel?: string;
};

/**
 * Fetches detailed post data using the posts API
 * @param postIds - Array of post IDs to fetch
 * @param hostname - The hostname to use for the API call
 * @param property - The property to use for the API call
 * @returns Promise<DetailedPost[]> - Array of detailed posts
 */
export const fetchDetailedPosts = async (
  postIds: string[],
  hostname: string,
  property: string
): Promise<DetailedPost[]> => {
  try {
    const idsParam = postIds.join(",");

    const detailResponse = await fetch(
      `https://${hostname}/api/properties/${property}/posts?ids=${idsParam}&limit=${postIds.length}&withBody=true`
    );

    if (!detailResponse.ok) {
      throw new Error(
        `Detailed posts request failed: ${detailResponse.status}`
      );
    }

    const detailData = await detailResponse.json();

    if (!detailData.data?.articles) {
      throw new Error("No detailed posts available");
    }

    return detailData.data.articles.map((article: any) => ({
      id: article.id,
      url: `https://${hostname}${article.path}`,
      title: article.title,
      coverImage: {
        path: article.cover?.image?.value?.path || "",
        url:
          article.cover?.image?.value?.url ||
          `https://images2.minutemediacdn.com/image/upload/${article.cover?.image?.value?.path}`,
        caption: article.cover?.image?.value?.caption || "",
        credit: article.cover?.image?.value?.credit || "",
        description: article.cover?.image?.value?.description || "",
      },
      author: article.authors?.owner?.name || "Unknown Author",
      updatedAt: article.updatedAt || article.createdAt,
      description:
        decodeHtmlEntities(article.intro?.replace(/<[^>]*>/g, "")) ||
        article.seoDescription ||
        "",
      body:
        article.body
          ?.map((block: any) =>
            block.type === "inline-text"
              ? decodeHtmlEntities(block.value?.html?.replace(/<[^>]*>/g, ""))
              : ""
          )
          .filter((text: string) => text.trim())
          .join("\n\n") || "",
      authorImage: article.authors?.owner?.authorInfo?.image?.value
        ? {
            path: article.authors.owner.authorInfo.image.value.path,
            url: article.authors.owner.authorInfo.image.value.url,
            caption: article.authors.owner.authorInfo.image.value.caption || "",
            credit: article.authors.owner.authorInfo.image.value.credit || "",
            description:
              article.authors.owner.authorInfo.image.value.description || "",
          }
        : null,
    }));
  } catch (error) {
    console.warn(
      `[MM Recirculation Widget] Detailed posts fetch failed: ${error.message}`
    );
    return [];
  }
};

/**
 * Fetches recommendation articles using auto-tags with improved error handling
 * @param pageConfig - Page configuration containing auto-tags
 * @param limit - Maximum number of articles to fetch
 * @param version - Optional version parameter for the API
 * @returns Promise<Post[]> - Array of recommended posts
 */
export const fetchRecommendationPostsWithAutoTags = async (
  pageConfig: PageConfig,
  limit: number,
  version?: string
): Promise<Post[]> => {
  try {
    const { property, id, autoTags } = pageConfig;

    if (autoTags.length === 0) {
      console.debug(
        "[MM Recirculation Widget] No auto-tags found so not fetching recommendations"
      );
      return [];
    }

    const queryParams: Record<string, string> = {
      property: property || "",
      currentPostID: id,
      autoTags: JSON.stringify(autoTags || []),
      limit: `${limit}`,
    };

    if (version) {
      queryParams.v = version;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const response = await fetch(
      `${getRecommendationEndpoint()}?${queryString}`
    );

    if (!response.ok) {
      throw new Error(
        `Auto-tags recommendations request failed: ${response.status}`
      );
    }

    const { data } = await response.json();

    if (!data || !data.posts) {
      throw new Error("No recommendations available");
    }

    return data.posts.map((post: any) => ({
      id: post.id,
      url: post.url,
      title: post.title,
      coverImage: post.coverImage,
      author: post.author,
      updatedAt: post.updatedAt,
    }));
  } catch (error) {
    console.warn(
      `[MM Recirculation Widget] API fetch failed, using mock data: ${error.message}`
    );
    return [];
  }
};

/**
 * Fetches recommendation articles using manual tags
 * @param pageConfig - Page configuration containing manual tags
 * @param limit - Maximum number of articles to fetch
 * @param version - Version identifier for recommendations
 * @returns Promise<Post[]> - Array of recommended posts
 */
export const fetchRecommendationPostsWithManualTags = async (
  pageConfig: PageConfig,
  limit: number,
  version: string
): Promise<Post[]> => {
  try {
    const { property, manualTags, id } = pageConfig;

    if (!manualTags || manualTags.length === 0) {
      console.debug(
        "[MM Recirculation Widget] No manual tags found so not fetching recommendations"
      );
      return [];
    }

    const queryParams: Record<string, string> = {
      property: property || "",
      currentPostID: id,
      tags: manualTags.join(","),
      v: version,
      limit: `${limit}`,
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const response = await fetch(
      `${getRecommendationEndpoint()}?${queryString}`
    );

    if (!response.ok) {
      throw new Error(
        `Manual tags recommendations request failed: ${response.status}`
      );
    }

    const { data } = await response.json();

    if (!data || !data.posts) {
      throw new Error("No recommendations available");
    }

    return data.posts.map((post: any) => ({
      id: post.id,
      url: post.url,
      title: post.title,
      coverImage: post.coverImage,
      author: post.author,
      updatedAt: post.updatedAt,
    }));
  } catch (error) {
    console.warn(
      `[MM Recirculation Widget] API fetch failed, using mock data: ${error.message}`
    );
    return [];
  }
};

/**
 * Fetches BlueConic recommendation articles
 * @param limit - Maximum number of articles to fetch
 * @param property - Site property identifier
 * @param currentPostURL - Current post URL
 * @param blueConicUserID - BlueConic user ID (optional)
 * @returns Promise<BlueConicRecommendationResponse> - Response with posts and tracking pixel
 */
export const fetchBlueConicRecommendationArticles = async (
  limit: number,
  property: string,
  currentPostURL: string,
  blueConicUserID: string | null = null
): Promise<BlueConicRecommendationResponse> => {
  const queryParams = {
    property,
    currentPostURL,
    limit: `${limit}`,
  } as Record<string, string>;

  if (blueConicUserID) {
    queryParams.blueConicUserID = blueConicUserID;
  }

  const queryString = new URLSearchParams(queryParams).toString();

  const response = await fetch(
    `${getBlueConicRecommendationEndpoint()}?${queryString}`
  );

  if (!response.ok) {
    throw new Error(
      `BlueConic recommendations request failed: ${response.status}`
    );
  }

  const { data } = await response.json();

  if (!data || !data.posts) {
    throw new Error("No recommendations available");
  }

  const posts = data.posts.map((post: any) => ({
    id: post.id,
    url: post.url,
    title: post.title,
    coverImage: post.coverImage,
    author: post.author,
    updatedAt: post.updatedAt,
  }));

  return {
    posts,
    trackingPixel: data.trackingPixel,
  };
};
